import { localAgentCatalogLaunchStatusSchema } from "./local-agent-catalog.js";
import * as z$1 from "zod/v4";
//#region src/schemas/runtime/agent-controller-interface.ts
const ACI_BOUNDED_ID_SCHEMA = z$1.string().trim().min(1).max(256);
const ACI_BOUNDED_LABEL_SCHEMA = z$1.string().trim().min(1).max(256);
const ACI_BOUNDED_TEXT_SCHEMA = z$1.string().trim().min(1).max(32e3);
const ACI_COMMAND_PATH_SCHEMA = z$1.string().trim().min(1).max(4096);
const ACI_ISO_DATE_TIME_SCHEMA = z$1.iso.datetime({ offset: true });
const AGENT_CONTROLLER_USAGE_LIMIT_STALE_AFTER_MS = 20 * 6e4;
const agentControllerUsageLimitStatusSchema = z$1.enum([
	"available",
	"exhausted",
	"stale",
	"unavailable",
	"unsupported"
]);
const agentControllerUsageProviderSchema = z$1.strictObject({
	displayName: ACI_BOUNDED_LABEL_SCHEMA,
	id: ACI_BOUNDED_ID_SCHEMA
});
const agentControllerUsageLimitSchema = z$1.strictObject({
	key: ACI_BOUNDED_ID_SCHEMA,
	label: ACI_BOUNDED_LABEL_SCHEMA,
	observedAt: ACI_ISO_DATE_TIME_SCHEMA,
	provider: agentControllerUsageProviderSchema.optional(),
	resetsAt: ACI_ISO_DATE_TIME_SCHEMA.nullable(),
	status: agentControllerUsageLimitStatusSchema,
	usedPercent: z$1.number().finite().min(0).max(100).nullable()
}).superRefine((value, context) => {
	if ((value.status === "available" || value.status === "exhausted") && value.usedPercent === null) context.addIssue({
		code: "custom",
		message: `${value.status} usage limits require usedPercent`,
		path: ["usedPercent"]
	});
	if (value.status === "stale" && value.usedPercent === null) context.addIssue({
		code: "custom",
		message: "stale usage limits require a last-known usedPercent",
		path: ["usedPercent"]
	});
	if (value.status === "exhausted" && value.usedPercent !== 100) context.addIssue({
		code: "custom",
		message: "exhausted usage limits require usedPercent to equal 100",
		path: ["usedPercent"]
	});
	if (value.status === "available" && value.usedPercent === 100) context.addIssue({
		code: "custom",
		message: "available usage limits require usedPercent below 100",
		path: ["usedPercent"]
	});
	if ((value.status === "unavailable" || value.status === "unsupported") && (value.usedPercent !== null || value.resetsAt !== null)) context.addIssue({
		code: "custom",
		message: `${value.status} usage limits cannot invent usage or reset facts`
	});
});
const agentControllerUsageLimitsSchema = z$1.array(agentControllerUsageLimitSchema).max(32).superRefine((limits, context) => {
	const keys = /* @__PURE__ */ new Set();
	for (const [index, limit] of limits.entries()) {
		if (keys.has(limit.key)) context.addIssue({
			code: "custom",
			message: "usage limit keys must be unique within one controller",
			path: [index, "key"]
		});
		keys.add(limit.key);
	}
});
const agentControllerInterfaceStatusSchema = z$1.enum([
	"disabled",
	"error",
	"ready",
	"unavailable",
	"unknown",
	"warning"
]);
const agentControllerInterfaceAuthStatusSchema = z$1.enum([
	"authenticated",
	"unauthenticated",
	"unknown"
]);
const agentControllerInterfaceAuthSnapshotSchema = z$1.strictObject({
	email: z$1.string().trim().email().max(320).optional(),
	label: ACI_BOUNDED_LABEL_SCHEMA.optional(),
	status: agentControllerInterfaceAuthStatusSchema,
	type: ACI_BOUNDED_ID_SCHEMA.optional()
});
z$1.enum([
	"failed",
	"not_run",
	"succeeded"
]);
const agentControllerInterfaceCheckSnapshotSchema = z$1.discriminatedUnion("status", [
	z$1.strictObject({
		lastSucceededAt: ACI_ISO_DATE_TIME_SCHEMA.nullable(),
		status: z$1.literal("failed")
	}),
	z$1.strictObject({
		lastSucceededAt: z$1.null(),
		status: z$1.literal("not_run")
	}),
	z$1.strictObject({
		lastSucceededAt: ACI_ISO_DATE_TIME_SCHEMA,
		status: z$1.literal("succeeded")
	})
]);
const agentControllerInterfaceVersionAdvisoryStatusSchema = z$1.enum([
	"behind_latest",
	"current",
	"unknown"
]);
const agentControllerInterfaceVersionAdvisorySchema = z$1.strictObject({
	checkedAt: ACI_ISO_DATE_TIME_SCHEMA.nullable(),
	currentVersion: ACI_BOUNDED_ID_SCHEMA.nullable(),
	latestVersion: ACI_BOUNDED_ID_SCHEMA.nullable(),
	message: ACI_BOUNDED_TEXT_SCHEMA.nullable(),
	status: agentControllerInterfaceVersionAdvisoryStatusSchema,
	updateCommand: ACI_BOUNDED_TEXT_SCHEMA.nullable()
});
const agentControllerInterfaceModelSchema = z$1.strictObject({
	displayName: ACI_BOUNDED_LABEL_SCHEMA,
	id: ACI_BOUNDED_ID_SCHEMA
});
const agentControllerInterfaceSkillSchema = z$1.strictObject({
	description: ACI_BOUNDED_TEXT_SCHEMA.optional(),
	displayName: ACI_BOUNDED_LABEL_SCHEMA.optional(),
	id: ACI_BOUNDED_ID_SCHEMA
});
const agentControllerInterfaceCapabilitiesSchema = z$1.strictObject({
	approvals: z$1.boolean(),
	cancel: z$1.boolean(),
	interrupt: z$1.boolean(),
	modelSelection: z$1.boolean(),
	nativeEventStream: z$1.boolean(),
	permissionModes: z$1.boolean(),
	structuredUserInput: z$1.boolean()
});
const agentControllerInterfaceLaunchSnapshotSchema = z$1.strictObject({
	argsPrefix: z$1.array(ACI_BOUNDED_TEXT_SCHEMA).max(64),
	commandPath: ACI_COMMAND_PATH_SCHEMA.nullable(),
	installChoiceCount: z$1.number().int().nonnegative(),
	status: localAgentCatalogLaunchStatusSchema
});
const agentControllerInterfaceNativeInterfaceSchema = z$1.enum([
	"acp",
	"claude-agent-sdk",
	"claude-cli",
	"codex-app-server",
	"local-cli"
]);
const agentControllerInterfaceSnapshotSchema = z$1.strictObject({
	agentControllerRef: ACI_BOUNDED_ID_SCHEMA,
	auth: agentControllerInterfaceAuthSnapshotSchema,
	capabilities: agentControllerInterfaceCapabilitiesSchema,
	check: agentControllerInterfaceCheckSnapshotSchema.optional(),
	checkedAt: ACI_ISO_DATE_TIME_SCHEMA,
	controllerId: ACI_BOUNDED_ID_SCHEMA,
	displayName: ACI_BOUNDED_LABEL_SCHEMA,
	launch: agentControllerInterfaceLaunchSnapshotSchema,
	message: ACI_BOUNDED_TEXT_SCHEMA.nullable(),
	models: z$1.array(agentControllerInterfaceModelSchema).max(256),
	nativeInterface: agentControllerInterfaceNativeInterfaceSchema,
	schemaVersion: z$1.literal(1),
	skills: z$1.array(agentControllerInterfaceSkillSchema).max(512),
	status: agentControllerInterfaceStatusSchema,
	usageLimits: agentControllerUsageLimitsSchema.optional(),
	usageLimitsSupportMatrixVersion: z$1.int().positive().optional(),
	version: ACI_BOUNDED_ID_SCHEMA.nullable(),
	versionAdvisory: agentControllerInterfaceVersionAdvisorySchema.optional()
});
//#endregion
export { AGENT_CONTROLLER_USAGE_LIMIT_STALE_AFTER_MS, agentControllerInterfaceAuthSnapshotSchema, agentControllerInterfaceAuthStatusSchema, agentControllerInterfaceCapabilitiesSchema, agentControllerInterfaceCheckSnapshotSchema, agentControllerInterfaceLaunchSnapshotSchema, agentControllerInterfaceModelSchema, agentControllerInterfaceNativeInterfaceSchema, agentControllerInterfaceSkillSchema, agentControllerInterfaceSnapshotSchema, agentControllerInterfaceStatusSchema, agentControllerInterfaceVersionAdvisorySchema, agentControllerInterfaceVersionAdvisoryStatusSchema, agentControllerUsageLimitSchema, agentControllerUsageLimitStatusSchema, agentControllerUsageLimitsSchema, agentControllerUsageProviderSchema };

//# sourceMappingURL=agent-controller-interface.js.map